import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  AcademicCapIcon, 
  EyeIcon, 
  EyeSlashIcon,
  CreditCardIcon,
  ClipboardDocumentCheckIcon,
  TruckIcon,
  SparklesIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  UserGroupIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { authApi } from '@/api/authApi'
import toast from 'react-hot-toast'

// Email + Password schema
const emailPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

// Phone + PIN schema  
const phonePinSchema = z.object({
  phoneNumber: z.string().min(1, 'Phone number is required'),
  pin: z.string().length(4, 'PIN must be exactly 4 digits').regex(/^\d{4}$/, 'PIN must contain only numbers'),
})

type EmailPasswordData = z.infer<typeof emailPasswordSchema>
type PhonePinData = z.infer<typeof phonePinSchema>

export default function LoginPage() {
  const { login, loginWithPhonePin } = useAuth()
  const navigate = useNavigate()
  // Removed error state - using toast notifications instead
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loginType, setLoginType] = useState<'email' | 'phone'>('email')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [countryCode, setCountryCode] = useState('KE')
  const [authMethod, setAuthMethod] = useState<'password' | 'pin'>('password')
  const [pin, setPin] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<EmailPasswordData>({
    resolver: zodResolver(emailPasswordSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })


  // Helper function to get phone code from country code
  const getCountryPhoneCode = (code: string) => {
    const phoneCodes: Record<string, string> = {
      'KE': '254', 'UG': '256', 'TZ': '255', 'RW': '250', 'US': '1', 'GB': '44'
    }
    return phoneCodes[code] || '254'
  }

  // Handle Email + Password submission
  const onEmailPasswordSubmit = async (data: EmailPasswordData) => {
    setIsLoading(true)
    try {
      const loginResponse = await login(data.email, data.password)
      await handleLoginSuccess(loginResponse)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle Phone + PIN submission
  const onPhonePinSubmit = async () => {
    if (!phoneNumber || phoneNumber.length < 8) {
      toast.error('Please enter a valid phone number')
      return
    }
    
    if (!pin || pin.length !== 4) {
      toast.error('Please enter a valid 4-digit PIN')
      return
    }

    setIsLoading(true)
    try {
      const fullPhoneNumber = `+${getCountryPhoneCode(countryCode)}${phoneNumber}`
      const loginResponse = await loginWithPhonePin(fullPhoneNumber, pin)
      await handleLoginSuccess(loginResponse)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to get dashboard route based on user role
  const getDashboardRouteForRole = (role: string): string => {
    const normalizedRole = role?.toLowerCase()?.replace(/[_\s]/g, '') || ''

    if (normalizedRole.includes('superadmin') || normalizedRole === 'super_admin') {
      return '/admin/dashboard'
    }
    if (normalizedRole.includes('schooladmin') || normalizedRole === 'school_admin') {
      return '/schoolAdmin/dashboard'
    }
    if (normalizedRole.includes('teacher')) {
      return '/teacher/dashboard'
    }
    if (normalizedRole.includes('parent')) {
      return '/parent/dashboard'
    }
    if (normalizedRole.includes('driver')) {
      return '/driver/dashboard'
    }
    if (normalizedRole.includes('student')) {
      return '/student/dashboard'
    }

    // Fallback to school admin dashboard
    return '/schoolAdmin/dashboard'
  }

  // Handle login success response
  const handleLoginSuccess = async (loginResponse: any) => {
    // Handle 2FA requirement
    if (loginResponse.requiresTwoFactor) {
      toast.info('Please complete two-factor authentication')
      // Navigate to 2FA page
      navigate('/two-factor', {
        state: {
          userId: loginResponse.userId,
          userType: loginResponse.userType
        }
      })
      return
    }

    // Check if user needs critical onboarding (password or PIN setup)
    const needsCriticalSetup = loginResponse.onboardingStatus?.needsPasswordSetup ||
                               loginResponse.onboardingStatus?.needsPinSetup

    if (loginResponse.requiresOnboarding && needsCriticalSetup) {
      // Redirect to onboarding flow for critical security setup
      navigate('/onboard', {
        replace: true,
        state: {
          onboardingStatus: loginResponse.onboardingStatus,
          nextStep: loginResponse.onboardingStatus?.nextStep
        }
      })
    } else {
      // Navigate to the appropriate dashboard based on user role
      const userRole = loginResponse.user?.role || ''
      const dashboardRoute = getDashboardRouteForRole(userRole)
      console.log('Redirecting to dashboard:', dashboardRoute, 'for role:', userRole)
      navigate(dashboardRoute, { replace: true })
    }
  }

  // Handle forgot password submission
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setForgotPasswordLoading(true)
    try {
      await authApi.forgotPassword(forgotPasswordEmail)
      toast.success('Password reset instructions sent to your email')
      setShowForgotPassword(false)
      setForgotPasswordEmail('')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send reset email')
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const features = [
    {
      icon: SparklesIcon,
      title: 'AI-Powered Analytics',
      description: 'Smart insights into student behavior patterns and attendance trends'
    },
    {
      icon: CreditCardIcon,
      title: 'RFID Smart Wristbands',
      description: 'Seamless student identification with contactless technology'
    },
    {
      icon: ClipboardDocumentCheckIcon,
      title: 'Automated Attendance',
      description: 'Real-time classroom and activity tracking across all locations'
    },
    {
      icon: TruckIcon,
      title: 'Live Transport Tracking',
      description: 'GPS-enabled bus monitoring with parent notifications'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and multi-factor authentication'
    },
    {
      icon: UserGroupIcon,
      title: 'Multi-Tenant Platform',
      description: 'Manage multiple schools with isolated data and permissions'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-gradient-to-r from-green-400 to-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Geometric Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.3))] opacity-30" />
      
      <div className="min-h-screen flex relative z-10">
        {/* Left Side - Features */}
        <div className="hidden lg:flex lg:flex-1 lg:flex-col lg:justify-center lg:px-8 xl:px-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-md mx-auto"
          >
            <div className="text-center mb-12">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, type: "spring", stiffness: 100, damping: 15 }}
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl shadow-2xl mb-6 relative"
              >
                <AcademicCapIcon className="h-10 w-10 text-white" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700 rounded-3xl animate-pulse opacity-20"></div>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-5xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent"
              >
                SmartTag School
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl text-gray-700 mt-3 font-medium"
              >
                Smart Wristbands, <span className="text-indigo-600">Smarter Schools</span>
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-500"
              >
                <div className="flex items-center">
                  <GlobeAltIcon className="h-4 w-4 mr-1" />
                  <span>Global Platform</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-4 w-4 mr-1" />
                  <span>Enterprise Grade</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  <span>Multi-Tenant</span>
                </div>
              </motion.div>
            </div>

            <div className="grid gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="group"
                >
                  <div className="flex items-start space-x-4 p-5 rounded-2xl bg-white/60 backdrop-blur-md border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:bg-white/80">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <feature.icon className="h-6 w-6 text-indigo-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-gray-900 group-hover:text-indigo-700 transition-colors">{feature.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-12 text-center"
            >
              <p className="text-sm text-gray-500">
                A Serix Holding Limited Product
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 xl:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-md w-full mx-auto"
          >
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, type: "spring", stiffness: 100, damping: 15 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-2xl mb-4"
              >
                <AcademicCapIcon className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                SmartTag School
              </h2>
              <p className="text-sm text-gray-600 mt-2 font-medium">
                Platform Administration
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-xl shadow-2xl rounded-3xl border border-white/50 p-8 relative overflow-hidden">
              {/* Form Background Accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full transform translate-x-16 -translate-y-16 opacity-50"></div>
              <div className="hidden lg:block text-center mb-8 relative z-10">
                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-3xl font-black text-gray-900"
                >
                  Welcome back
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="text-gray-600 mt-2 font-medium"
                >
                  Sign in to your <span className="text-indigo-600 font-semibold">platform account</span>
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-blue-100 text-green-700 text-xs font-medium"
                >
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  Platform Online
                </motion.div>
              </div>

              <form className="space-y-6" onSubmit={(e) => {
                e.preventDefault()
                if (loginType === 'phone' && authMethod === 'pin') {
                  onPhonePinSubmit()
                } else {
                  handleSubmit(onEmailPasswordSubmit)(e)
                }
              }}>
                {/* Removed Alert component - using toast notifications instead */}

                <div className="space-y-6 relative z-10">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-700">
                        {loginType === 'email' ? 'Email Address' : 'Phone Number'}
                      </label>
                      <div className="flex items-center space-x-1 bg-gray-100/80 rounded-xl p-1 backdrop-blur-sm">
                        <motion.button
                          type="button"
                          onClick={() => {
                            setLoginType('email')
                            setValue('identifier', '')
                            setPhoneNumber('')
                          }}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                            loginType === 'email' 
                              ? 'bg-white text-indigo-600 shadow-lg scale-105' 
                              : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                          }`}
                          disabled={isLoading}
                          whileHover={{ scale: loginType !== 'email' ? 1.05 : 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                          <span>Email</span>
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => {
                            setLoginType('phone')
                            setValue('identifier', '')
                          }}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                            loginType === 'phone' 
                              ? 'bg-white text-indigo-600 shadow-lg scale-105' 
                              : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                          }`}
                          disabled={isLoading}
                          whileHover={{ scale: loginType !== 'phone' ? 1.05 : 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <DevicePhoneMobileIcon className="h-4 w-4" />
                          <span>Phone</span>
                        </motion.button>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <AnimatePresence mode="wait">
                        {loginType === 'email' ? (
                          <motion.div
                            key="email"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Input
                              {...register('email')}
                              type="email"
                              placeholder="admin@smarttagschool.com"
                              error={errors.email?.message}
                              disabled={isLoading}
                              className="h-12 text-lg bg-gray-50/50 border-2 border-gray-200 rounded-xl transition-all duration-300 hover:border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white shadow-sm"
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="phone"
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <PhoneNumberInput
                              value={phoneNumber}
                              countryCode={countryCode}
                              onChange={(phone, country) => {
                                setPhoneNumber(phone)
                                setCountryCode(country)
                              }}
                              placeholder="Enter your registered phone number"
                              error={errors.identifier?.message}
                              disabled={isLoading}
                              className="h-12 text-lg bg-gray-50/50 border-2 border-gray-200 rounded-xl transition-all duration-300 hover:border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white shadow-sm"
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full opacity-30"></div>
                      </div>
                    </div>
                  </motion.div>

                  {/* PIN/Password Selector for Phone Login */}
                  {loginType === 'phone' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.15 }}
                      className="mb-4"
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Authentication Method
                      </label>
                      <div className="flex items-center space-x-1 bg-gray-100/80 rounded-xl p-1 backdrop-blur-sm">
                        <motion.button
                          type="button"
                          onClick={() => setAuthMethod('password')}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                            authMethod === 'password' 
                              ? 'bg-white text-indigo-600 shadow-lg scale-105' 
                              : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                          }`}
                          disabled={isLoading}
                          whileHover={{ scale: authMethod !== 'password' ? 1.05 : 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span>Password</span>
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => setAuthMethod('pin')}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                            authMethod === 'pin' 
                              ? 'bg-white text-indigo-600 shadow-lg scale-105' 
                              : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                          }`}
                          disabled={isLoading}
                          whileHover={{ scale: authMethod !== 'pin' ? 1.05 : 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span>PIN</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-3">
                      {loginType === 'phone' && authMethod === 'pin' ? '4-Digit PIN' : 'Password'}
                    </label>
                    <div className="relative">
                      <AnimatePresence mode="wait">
                        {loginType === 'phone' && authMethod === 'pin' ? (
                          <motion.div
                            key="pin"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={4}
                              value={pin}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                                setPin(value)
                              }}
                              placeholder="Enter your 4-digit PIN"
                              disabled={isLoading}
                              className="h-12 text-lg bg-gray-50/50 border-2 border-gray-200 rounded-xl text-center tracking-widest transition-all duration-300 hover:border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white shadow-sm font-mono"
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="password"
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                            className="relative"
                          >
                            <Input
                              {...register('password')}
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Enter your secure password"
                              error={errors.password?.message}
                              disabled={isLoading}
                              className="h-12 text-lg bg-gray-50/50 border-2 border-gray-200 rounded-xl pr-12 transition-all duration-300 hover:border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white shadow-sm"
                            />
                            <motion.button
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-indigo-600 transition-all duration-200 hover:scale-110"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={isLoading}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              {showPassword ? (
                                <EyeSlashIcon className="h-5 w-5" />
                              ) : (
                                <EyeIcon className="h-5 w-5" />
                              )}
                            </motion.button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                </div>

                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex items-center justify-between relative z-10"
                >
                  <div className="flex items-center group">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-5 w-5 text-indigo-600 focus:ring-indigo-500/50 border-gray-300 rounded-md transition-all duration-200 group-hover:border-indigo-400"
                      disabled={isLoading}
                    />
                    <label htmlFor="remember-me" className="ml-3 block text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors cursor-pointer">
                      Keep me signed in
                    </label>
                  </div>

                  <div className="text-sm">
                    <button 
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="font-semibold text-indigo-600 hover:text-indigo-500 transition-all duration-200 hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white font-bold text-lg rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    {/* Button Background Animation */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    <span className="relative z-10 flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white mr-3"></div>
                          Signing you in...
                        </>
                      ) : (
                        <>
                          <AcademicCapIcon className="h-5 w-5 mr-3" />
                          Access Platform
                        </>
                      )}
                    </span>
                  </Button>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="relative"
                >
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t-2 border-gradient-to-r from-transparent via-gray-200 to-transparent" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 py-1 bg-white/80 text-gray-600 font-medium rounded-full backdrop-blur-sm">
                      Secure Platform Access
                    </span>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="text-center space-y-2"
                >
                  <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-3 w-3 mr-1" />
                      <span>256-bit SSL</span>
                    </div>
                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                    <div className="flex items-center">
                      <SparklesIcon className="h-3 w-3 mr-1" />
                      <span>SOC 2 Compliant</span>
                    </div>
                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-3 w-3 mr-1" />
                      <span>99.9% Uptime</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 font-medium">
                    Trusted by <span className="text-indigo-600 font-bold">1,000+</span> educational institutions worldwide
                  </p>
                </motion.div>
              </form>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="mt-8 text-center space-y-3"
            >
              <div className="flex items-center justify-center space-x-1 text-xs text-gray-500">
                <span>Powered by</span>
                <span className="font-bold text-indigo-600">SmartTag Technology</span>
                <div className="w-1 h-1 bg-indigo-400 rounded-full animate-pulse"></div>
              </div>
              <p className="text-xs text-gray-500">
                © 2024 SmartTag School. A <span className="font-semibold">Serix Holding Limited</span> Product
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                <a href="#" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <a href="#" className="hover:text-indigo-600 transition-colors">Terms of Service</a>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <a href="#" className="hover:text-indigo-600 transition-colors">Support</a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reset Your Password</h3>
              <p className="text-gray-600 mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <div className="space-y-4">
                <Input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full"
                />
                
                <div className="flex space-x-3">
                  <Button
                    onClick={handleForgotPassword}
                    loading={forgotPasswordLoading}
                    disabled={forgotPasswordLoading}
                    className="flex-1"
                  >
                    Send Reset Email
                  </Button>
                  <Button
                    onClick={() => {
                      setShowForgotPassword(false)
                      setForgotPasswordEmail('')
                    }}
                    variant="ghost"
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}